// src/components/RawTable.tsx
import { Box, TablePagination } from "@material-ui/core";
import MaterialTable from "material-table";
import useFetchGraphData from "../hooks/useFetchGraphData";
import { titleize } from "inflected";
import { useMemo } from "react";
import styled from "styled-components/macro";
import IconButton from "@material-ui/core/IconButton";
import {
  ChevronLeft,
  ChevronRight,
  FirstPage,
  LastPage,
} from "@material-ui/icons";
import useTheme from "@material-ui/core/styles/useTheme";

const TableWrapper = styled.div`
  width: 100%;
  height: 400px;
  overflow: auto;

  .MuiTableCell-root {
    padding: 4px 8px;
  }

  .MuiToolbar-root {
    min-height: 40px !important;
  }

  .MuiTable-root {
    font-size: 0.75rem;
  }

  /* material-table gives every column's sort label the active column's
     direction; a click on an inactive column always sorts ascending, so show
     its arrow that way (MUI draws "asc" as a 180 degree rotation). */
  .MuiTableSortLabel-root:not(.MuiTableSortLabel-active)
    .MuiTableSortLabel-icon {
    transform: rotate(180deg) !important;
  }
`;

const getColumns = (data) => {
  if (!data?.length) return [];

  /* --- very small heuristic column builder --------------------------- */
  const columns =
    data?.length === 0
      ? []
      : Object.keys(data[0]).map((k) => ({
          title: titleize(k),
          field: k,
          type: typeof data[0][k] === "number" ? "numeric" : "string",
        }));

  return columns;
};

function PaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onChangePage } = props;

  const handleFirstPage = (event) => onChangePage(event, 0);
  const handleBack = (event) => onChangePage(event, page - 1);
  const handleNext = (event) => onChangePage(event, page + 1);
  const handleLastPage = (event) =>
    onChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));

  return (
    <div style={{ flexShrink: 0, marginLeft: theme.spacing(2.5) }}>
      <IconButton
        onClick={handleFirstPage}
        disabled={page === 0}
        aria-label="First Page"
      >
        <FirstPage />
      </IconButton>
      <IconButton
        onClick={handleBack}
        disabled={page === 0}
        aria-label="Previous Page"
      >
        <ChevronLeft />
      </IconButton>
      <IconButton
        onClick={handleNext}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="Next Page"
      >
        <ChevronRight />
      </IconButton>
      <IconButton
        onClick={handleLastPage}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="Last Page"
      >
        <LastPage />
      </IconButton>
    </div>
  );
}

export default function RawTable({
  endPoint,
  params,
  columns = [],
  /* Decimal places for non-integer values. One is right for the concentration
     tables this started with; precipitation in inches needs two, or 0.48 in of
     rain reads as 0.5. */
  decimals = 1,
}) {
  const { data = [], isLoading, isError } = useFetchGraphData(params, endPoint);

  const safeData = useMemo(
    () =>
      data.map((row) =>
        Object.fromEntries(
          Object.entries(row).map(([k, v]) => {
            if (v == null) return [k, v];
            if (typeof v === "object") {
              // pick something meaningful or stringify
              return [k, v.name ?? v.id ?? JSON.stringify(v)];
            }
            if (typeof v === "number" && !Number.isInteger(v)) {
              const factor = 10 ** decimals;
              return [k, Math.round(v * factor) / factor];
            }
            return [k, v];
          })
        )
      ),
    [data, decimals]
  );

  const tableColumns = useMemo(() => {
    if (columns.length) return columns;
    return getColumns(data);
  }, [data, columns]);

  if (isError) return <Box py={4}>Error loading table.</Box>;

  return (
    <TableWrapper>
      <MaterialTable
        title={null}
        /* material-table mutates whatever columns and data it is handed - it
           writes its own `tableData` onto every column and row, including the
           column widths it measures. Handing it our memoized arrays lets those
           mutations persist and compound across re-renders, which bloats the
           widths and eventually locks the tab up after a few filter changes.
           Fresh shallow copies each render keep the mutations off our objects.
           Several pages already sidestepped this by rebuilding their columns
           inline; doing it here covers all of them. */
        columns={tableColumns.map((column) => ({ ...column }))}
        data={safeData.map((row) => ({ ...row }))}
        isLoading={isLoading}
        options={{
          exportButton: true,
          exportAllData: true,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          sorting: true,
          filtering: false,
          toolbar: true,
          padding: "dense",
          headerStyle: {
            fontWeight: "bold",
            fontSize: "0.75rem",
            whiteSpace: "nowrap",
          },
          rowStyle: {
            fontSize: "0.75rem",
          },
        }}
        components={{
          Pagination: (props) => (
            <TablePagination {...props} ActionsComponent={PaginationActions} />
          ),
        }}
      />
    </TableWrapper>
  );
}
